// Enhanced Types for Website Builder

// Theme Configuration
export interface SchoolTheme {
    colors: {
        primary: string;      // Main brand color (header, buttons)
        secondary: string;    // Accent/CTA color  
        accent: string;       // Highlights, gold accents
        background: string;   // Page background
        text: string;         // Primary text color
        textMuted: string;    // Secondary/muted text
    };
    fonts: {
        heading: string;      // Google Font name for headings
        body: string;         // Google Font name for body text
    };
    borderRadius: 'none' | 'small' | 'medium' | 'large' | 'full';
    buttonStyle: 'solid' | 'outline' | 'ghost' | 'gradient';
}

// Section Visibility Configuration
export interface SectionVisibility {
    hero: boolean;
    about: boolean;
    features: boolean;
    programs: boolean;
    team: boolean;
    facilities: boolean;
    gallery: boolean;
    news: boolean;
    testimonials: boolean;
    downloads: boolean;
    contact: boolean;
    cta: boolean;
    stats: boolean;
}

// Navigation Item
export interface NavItem {
    id: string;
    label: string;
    href: string;           // Internal (#about) or external (https://...)
    visible: boolean;
    isExternal?: boolean;
    children?: NavItem[];   // For dropdown menus
}

// Layout Configuration per Section
export interface LayoutConfig {
    hero: {
        style: 'centered' | 'left-aligned' | 'split-image' | 'video-background';
        showTagline: boolean;
        showStats: boolean;
        overlayOpacity: number; // 0-100
    };
    programs: {
        columns: 2 | 3 | 4;
        cardStyle: 'minimal' | 'detailed' | 'image-overlay';
    };
    team: {
        layout: 'grid' | 'carousel' | 'list';
        showBio: boolean;
        columns: 2 | 3 | 4;
    };
    gallery: {
        layout: 'grid' | 'masonry' | 'carousel';
        columns: 2 | 3 | 4;
        showCategories: boolean;
    };
    features: {
        layout: 'grid' | 'list' | 'cards';
        columns: 2 | 3 | 4;
        showIcons: boolean;
    };
}

// Contact Information
export interface ContactInfo {
    phone?: string;
    email?: string;
    address?: string;
    poBox?: string;
    whatsapp?: string;
    admissionsPhone?: string;
    admissionsEmail?: string;
    generalPhone?: string;
    officeHours?: string;
    // Extended contact fields
    image?: string;
    title?: string;
    subtitle?: string;
    hours?: string;
    mapUrl?: string;
    // Social Media
    facebook?: string;
    instagram?: string;
    twitter?: string;
    linkedin?: string;
    youtube?: string;
    tiktok?: string;
}

// Full School Data Structure
export interface SchoolData {
    // Basic Info
    name?: string;
    tagline?: string;
    subdomain?: string;
    logoUrl?: string;
    description?: string;
    mission?: string;

    // Theme & Styling
    theme?: SchoolTheme;
    colors?: {
        primary: string;
        secondary: string;
        accent: string;
    };

    // Section Control
    sectionVisibility?: SectionVisibility;
    layoutConfig?: LayoutConfig;

    // Navigation
    nav?: NavItem[];

    // Content Sections
    hero?: {
        titlePrefix?: string;
        tagline?: string;
        subtitle?: string;
        ctaText?: string;
        ctaUrl?: string;
        backgroundImage?: string;
        foregroundImage?: string;
        images?: string[];
    };

    about?: {
        title?: string;
        description?: string;
        image?: string;
        sectionImage?: string;
        image1?: string;
        image2?: string;
    };

    features?: Array<{
        icon?: string;
        title?: string;
        desc?: string;
        description?: string;
    }>;

    programs?: Array<{
        title?: string;
        age?: string;
        desc?: string;
        description?: string;
        img?: string;
        image?: string;
    }>;

    team?: Array<{
        name?: string;
        role?: string;
        img?: string;
        image?: string;
        bio?: string;
    }>;

    facilities?: Array<{
        title?: string;
        desc?: string;
        description?: string;
        img?: string;
        image?: string;
        icon?: string;
    }>;

    gallery?: Array<{
        category?: string;
        img?: string;
        image?: string;
        title?: string;
        caption?: string;
    }>;

    news?: Array<{
        title?: string;
        date?: string;
        desc?: string;
        description?: string;
        img?: string;
        image?: string;
    }>;

    testimonials?: Array<{
        quote?: string;
        name?: string;
        role?: string;
        img?: string;
        image?: string;
    }>;

    downloads?: Array<{
        title?: string;
        desc?: string;
        description?: string;
        url?: string;
        fileType?: string;
        fileSize?: string;
    }>;

    stats?: Array<{
        value?: string;
        label?: string;
    }>;

    // Contact
    contact?: ContactInfo;

    // Labels (customizable text)
    labels?: {
        [key: string]: string;
    };

    // Meta
    meta?: {
        title?: string;
        description?: string;
        keywords?: string[];
        ogImage?: string;
    };

    // Flexible fallback for additional data
    [key: string]: any;
}

// Website Design Component Props
export interface WebsiteDesignProps {
    previewData: SchoolData;
    isEditMode?: boolean;
    onEditSection?: (section: string) => void;
    onUpdateField?: (field: string, value: any) => void;
}

// Design Registry Entry
export interface DesignMeta {
    id: string;
    name: string;
    description: string;
    category: 'modern' | 'classic' | 'playful' | 'corporate' | 'artistic' | 'academic';
    targetAudience: 'primary' | 'secondary' | 'mixed' | 'university' | 'vocational';
    features: string[];
    thumbnail?: string;
    component: React.ComponentType<WebsiteDesignProps>;
}

// Default Theme
export const DEFAULT_THEME: SchoolTheme = {
    colors: {
        primary: '#1e3a5f',
        secondary: '#228b22',
        accent: '#ffd700',
        background: '#ffffff',
        text: '#1e293b',
        textMuted: '#64748b'
    },
    fonts: {
        heading: 'Inter',
        body: 'Inter'
    },
    borderRadius: 'medium',
    buttonStyle: 'solid'
};

// Default Section Visibility
export const DEFAULT_SECTION_VISIBILITY: SectionVisibility = {
    hero: true,
    about: true,
    features: true,
    programs: true,
    team: true,
    facilities: true,
    gallery: true,
    news: true,
    testimonials: false,
    downloads: true,
    contact: true,
    cta: true,
    stats: true
};

// Default Navigation
export const DEFAULT_NAV: NavItem[] = [
    { id: 'about', label: 'About', href: '#about', visible: true },
    { id: 'team', label: 'Team', href: '#team', visible: true },
    { id: 'programs', label: 'Academics', href: '#programs', visible: true },
    { id: 'facilities', label: 'Facilities', href: '#facilities', visible: true },
    { id: 'gallery', label: 'Gallery', href: '#gallery', visible: true },
    { id: 'contact', label: 'Contact', href: '#contact', visible: true }
];

// Available Font Options
export const FONT_OPTIONS = [
    { name: 'Inter', category: 'modern', preview: 'The quick brown fox' },
    { name: 'Poppins', category: 'modern', preview: 'The quick brown fox' },
    { name: 'Roboto', category: 'modern', preview: 'The quick brown fox' },
    { name: 'Open Sans', category: 'modern', preview: 'The quick brown fox' },
    { name: 'Montserrat', category: 'modern', preview: 'The quick brown fox' },
    { name: 'Playfair Display', category: 'elegant', preview: 'The quick brown fox' },
    { name: 'Merriweather', category: 'elegant', preview: 'The quick brown fox' },
    { name: 'Lora', category: 'elegant', preview: 'The quick brown fox' },
    { name: 'Crimson Text', category: 'elegant', preview: 'The quick brown fox' },
    { name: 'Libre Baskerville', category: 'elegant', preview: 'The quick brown fox' },
    { name: 'Nunito', category: 'friendly', preview: 'The quick brown fox' },
    { name: 'Quicksand', category: 'friendly', preview: 'The quick brown fox' },
    { name: 'Comfortaa', category: 'friendly', preview: 'The quick brown fox' },
    { name: 'Fredoka One', category: 'playful', preview: 'The quick brown fox' },
    { name: 'Roboto Slab', category: 'formal', preview: 'The quick brown fox' },
    { name: 'Source Serif Pro', category: 'formal', preview: 'The quick brown fox' },
    { name: 'IBM Plex Serif', category: 'formal', preview: 'The quick brown fox' },
] as const;

// Color Presets for Quick Selection
export const COLOR_PRESETS = [
    {
        name: 'Oxford Prestige',
        primary: '#002147', // Oxford Blue
        secondary: '#1a3024', // Deep Forest
        accent: '#D4AF37',    // Antique Gold
        description: 'Traditional excellence and heritage'
    },
    {
        name: 'Cambridge Royal',
        primary: '#003b6f', // Royal Academic Blue
        secondary: '#537d91', // Cambridge-ish light
        accent: '#C0C0C0',    // Academic Silver
        description: 'Modern academic and trustworthy'
    },
    {
        name: 'Heritage Burgundy',
        primary: '#4a0404', // Deep Burgundy
        secondary: '#002147', // Navy companion
        accent: '#e3c263',    // Soft Gold
        description: 'Distinguished and historic'
    },
    {
        name: 'Evergreen Scholar',
        primary: '#0a2e1e', // Scholar Green
        secondary: '#334155', // Slate
        accent: '#facc15',    // Bright Gold
        description: 'Focus on growth and nature'
    },
    {
        name: 'Modern Slate',
        primary: '#1e293b', // Deep Slate
        secondary: '#4f46e5', // Indigo
        accent: '#38bdf8',    // Sky Blue highlight
        description: 'Contemporary, tech-forward design'
    },
    {
        name: 'Midnight Imperial',
        primary: '#0f172a', // Deepest Navy
        secondary: '#7c2d12', // Rust/Clay
        accent: '#fdba74',    // Peach Glow
        description: 'Sophisticated and unique'
    },
    {
        name: 'Academic Crimson',
        primary: '#7f1d1d', // Rich Crimson
        secondary: '#111827', // Black-Blue
        accent: '#fde047',    // Vibrant Yellow
        description: 'Bold, energetic, and proud'
    },
    {
        name: 'Pacific Academic',
        primary: '#0c4a6e', // Deep Ocean Blue
        secondary: '#0d9488', // Teal
        accent: '#e2e8f0',    // Soft Slate Silver
        description: 'Calm, professional, and clear'
    }
] as const;
